import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectRemindersError,
  setReminderTodo,
  clearError,
} from 'store/slices/remindersSlice';
import {
  selectAllTodos,
  selectTodosLoading,
} from 'store/slices/todosSlice';
import { apiClient, buildQueryString } from 'utils/api';
import { Badge } from 'components/badges';
import { Button, PrimaryButton } from 'components/buttons';
import { DebouncedSearchInput, ToggleField } from 'components/forms';
import { Skeleton } from 'components/layout/loading';
import { AlertError, AlertWarning } from 'components/layout/alerts';
import { REMINDER_STATUSES, REMINDER_TYPES } from 'constants/reminders';
import { formatSchedule } from 'utils/date';
import { formatReminderActiveWindow } from 'utils/reminders';

const hasTodoAssigned = (reminder) => Boolean(reminder?.todo);
const isAttachableReminder = (reminder) => (
  reminder?.status === REMINDER_STATUSES.ACTIVE || reminder?.status === REMINDER_STATUSES.PAUSED
);

function ReminderOptionListItem({ reminder, isSelected, onSelect, settings }) {
  const scheduleText = formatSchedule(reminder, settings, true);
  const activeWindowText = formatReminderActiveWindow(reminder, settings);

  return (
    <div
      className={classNames(
        'flex items-center rounded-md border p-4 transition-colors cursor-pointer',
        {
          'border-indigo-500 bg-indigo-900/20': isSelected,
          'border-gray-700 bg-gray-800 hover:bg-gray-700': !isSelected,
        }
      )}
      onClick={onSelect}
    >
      <div
        className={classNames(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
          {
            'border-indigo-500': isSelected,
            'border-gray-500': !isSelected,
          }
        )}
      >
        {isSelected && <div className="h-2 w-2 rounded-full bg-indigo-500" />}
      </div>
      <div className="ml-3 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-medium text-white">{reminder.subject}</p>
          {hasTodoAssigned(reminder) && <Badge size="sm" color="purple">Todo assigned</Badge>}
        </div>
        <p className="text-xs text-gray-400">{scheduleText}</p>
        {activeWindowText && <p className="text-xs text-gray-500">{activeWindowText}</p>}
      </div>
    </div>
  );
}

export default function AttachTodoToReminder({
  todoId,
  onSuccess,
  onCancel,
  showTodoInfo = false,
  cancelLabel = 'Cancel',
  successLabel,
  className = '',
}) {
  const dispatch = useDispatch();
  const reminderError = useSelector(selectRemindersError);

  const allTodos = useSelector(selectAllTodos);
  const loadingTodos = useSelector(selectTodosLoading);
  const isInitialLoadingTodos = loadingTodos && allTodos.length === 0;
  const user = useSelector((state) => state.auth.user);

  const [reminderResults, setReminderResults] = useState([]);
  const [loadingReminderResults, setLoadingReminderResults] = useState(false);
  const [reminderSearchError, setReminderSearchError] = useState(null);
  const [hasFetchedReminders, setHasFetchedReminders] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [todoControlsReminder, setTodoControlsReminder] = useState(false);
  const [searchFilterValue, setSearchFilterValue] = useState('');
  const [showRemindersWithTodo, setShowRemindersWithTodo] = useState(true);
  const [isAttaching, setIsAttaching] = useState(false);
  const reminderSearchRequestIdRef = useRef(0);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleSelectReminder = (reminder) => {
    setSelectedReminder(selectedReminder?._id === reminder._id ? null : reminder);
  };

  const fetchReminderResults = useCallback(async (nextSearchFilterValue) => {
    const normalizedSearch = nextSearchFilterValue.trim();
    const requestId = reminderSearchRequestIdRef.current + 1;

    reminderSearchRequestIdRef.current = requestId;

    if (!normalizedSearch) {
      setHasFetchedReminders(false);
      setReminderResults([]);
      setReminderSearchError(null);
      setLoadingReminderResults(false);
      return;
    }

    setHasFetchedReminders(true);
    setLoadingReminderResults(true);
    setReminderSearchError(null);

    try {
      const qs = buildQueryString({
        search: normalizedSearch,
        status: `${REMINDER_STATUSES.ACTIVE},${REMINDER_STATUSES.PAUSED}`,
      });
      const response = await apiClient.get(qs ? `/reminders?${qs}` : '/reminders');

      if (reminderSearchRequestIdRef.current !== requestId) {
        return;
      }

      setReminderResults((response.data?.data || []).filter(isAttachableReminder));
    } catch (error) {
      if (reminderSearchRequestIdRef.current !== requestId) {
        return;
      }

      setReminderResults([]);
      setReminderSearchError(error.response?.data || 'Failed to fetch reminders');
    } finally {
      if (reminderSearchRequestIdRef.current === requestId) {
        setLoadingReminderResults(false);
      }
    }
  }, []);

  const filteredReminders = useMemo(() => (
    reminderResults.filter((reminder) => {
      if (!showRemindersWithTodo && hasTodoAssigned(reminder)) return false;
      return true;
    })
  ), [reminderResults, showRemindersWithTodo]);

  useEffect(() => {
    if (!selectedReminder) return;
    const stillVisible = filteredReminders.some((r) => r._id === selectedReminder._id);
    if (!stillVisible) setSelectedReminder(null);
  }, [filteredReminders, selectedReminder]);

  const handleAttach = async () => {
    if (!selectedReminder || !todoId) return;
    try {
      setIsAttaching(true);
      await dispatch(setReminderTodo({
        reminderId: selectedReminder._id,
        todoId,
        todoControlsReminder: selectedReminder.type === REMINDER_TYPES.ONE_TIME ? false : todoControlsReminder,
      })).unwrap();

      if (onSuccess) onSuccess();
    } catch (_error) {
      // error is captured in Redux state via rejectWithValue and displayed in the UI
    } finally {
      setIsAttaching(false);
    }
  };

  const selectedTodo = allTodos.find((t) => t._id === todoId);
  const emptyMessage = !hasFetchedReminders
    ? 'Search for reminders to see results.'
    : (!showRemindersWithTodo && reminderResults.length > 0 && filteredReminders.length === 0)
      ? 'No reminders without todos found.'
      : 'No matching reminders found.';

  return (
    <div className={`flex h-full flex-col ${className}`}>
      {reminderError && (
        <div className="mb-4">
          <AlertError title="Error">{reminderError}</AlertError>
        </div>
      )}

      {reminderSearchError && (
        <div className="mb-4">
          <AlertError title="Error">{reminderSearchError}</AlertError>
        </div>
      )}

      {showTodoInfo && selectedTodo && (
        <div className="mb-4 rounded-lg border border-gray-700 bg-gray-800 p-4">
          <h3 className="text-sm font-medium text-gray-300">Selected Todo</h3>
          <p className="font-semibold text-white">{selectedTodo.name}</p>
        </div>
      )}

      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end">
        <DebouncedSearchInput
          id="attach-todo-search"
          label="Search reminders"
          placeholder="Search reminders..."
          value={searchFilterValue}
          onChange={(event) => {
            const nextValue = event.target.value;

            setSearchFilterValue(nextValue);
            fetchReminderResults(nextValue);
          }}
          disabled={isAttaching}
          className="w-full sm:flex-1"
        />
        <ToggleField
          id="show-reminders-with-todo"
          label="Show reminders with todos"
          checked={showRemindersWithTodo}
          onChange={setShowRemindersWithTodo}
          disabled={isAttaching}
          className="w-fit max-w-full sm:shrink-0"
        />
      </div>

      <div className="custom-scrollbar min-h-[200px] max-h-[400px] flex-1 space-y-4 overflow-y-auto pr-2">
        {loadingReminderResults && !isAttaching ? (
          <>
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
          </>
        ) : (
          <>
            {filteredReminders.map((reminder) => (
              <ReminderOptionListItem
                key={reminder._id}
                reminder={reminder}
                isSelected={selectedReminder?._id === reminder._id}
                onSelect={() => handleSelectReminder(reminder)}
                settings={user?.settings}
              />
            ))}
            {filteredReminders.length === 0 && (
              <p className="py-8 text-center text-gray-400">
                {emptyMessage}
              </p>
            )}
          </>
        )}
      </div>

      {hasTodoAssigned(selectedReminder) && (
        <div className="mt-4">
          <AlertWarning>
            This reminder already has a todo assigned. Assigning a new todo will replace the existing one.
          </AlertWarning>
        </div>
      )}

      <div className="mt-4">
        <ToggleField
          id="todo-controls-reminder"
          label="Todo controls reminder status"
          checked={selectedReminder?.type === REMINDER_TYPES.ONE_TIME ? false : todoControlsReminder}
          onChange={setTodoControlsReminder}
          disabled={isAttaching || loadingReminderResults || selectedReminder?.type === REMINDER_TYPES.ONE_TIME}
        />
        {selectedReminder?.type === REMINDER_TYPES.ONE_TIME && (
          <p className="mt-1.5 text-xs text-gray-500">
            Not available — one-time reminders complete automatically after firing.
          </p>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-y-2">
        <PrimaryButton
          onClick={handleAttach}
          disabled={!selectedReminder || !todoId || loadingReminderResults || isInitialLoadingTodos || isAttaching}
          className="w-full"
        >
          {isAttaching
            ? (selectedReminder ? 'Attaching...' : 'Processing...')
            : (successLabel || (selectedReminder ? 'Attach & Finish' : 'Finish'))}
        </PrimaryButton>

        {onCancel && (
          <Button
            onClick={onCancel}
            className="w-full"
            disabled={isAttaching}
          >
            {cancelLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
